import { useCallback, useEffect, useReducer } from 'react'
import { useMediaReloadRequests } from 'room/common/media-reload'
import {
	INITIAL_MEDIA_LOAD,
	MEDIA_LOAD_TIMEOUT_MS,
	MediaStatus,
	nextMediaLoad,
} from 'room/common/model/media-status'

/** The part of a wavesurfer the load needs; a wavesurfer is one. */
export type AudioFileLoader = {
	load: (url: string) => Promise<void>
	on: (event: 'ready' | 'error', listener: () => void) => () => void
}

export type AudioLoad = {
	status: MediaStatus
	/** Asks for the file again; only does anything after a failure. */
	retry: () => void
}

/**
 * Loads the recording into its player and says how that went - from what the
 * player itself signals, never from the lack of a signal:
 *
 * - `ready` (wavesurfer emits it after `decode`): the file is there;
 * - the `error` event, a rejected `load()` (a refused or failed request, a
 *   file that does not decode) or the media element's own error: failed;
 * - nothing for `MEDIA_LOAD_TIMEOUT_MS`: failed, so a hanging request does
 *   not leave a spinner on a screen forever.
 *
 * The load is started here, after the listeners are on, so no signal of it is
 * missed. A retry (the room's button, or the console's request through
 * `useMediaReloadRequests`) loads the file again, after a failure only.
 */
export const useAudioLoad = (
	loader: AudioFileLoader | null,
	url: string
): AudioLoad => {
	const [{ status, attempt }, dispatch] = useReducer(
		nextMediaLoad,
		INITIAL_MEDIA_LOAD
	)

	useEffect(() => {
		if (!loader) {
			return
		}
		dispatch('started')
		let current = true
		const timer = window.setTimeout(
			() => dispatch('timed-out'),
			MEDIA_LOAD_TIMEOUT_MS
		)
		const stopReady = loader.on('ready', () => {
			window.clearTimeout(timer)
			dispatch('loaded')
		})
		const stopError = loader.on('error', () => {
			window.clearTimeout(timer)
			dispatch('failed')
		})
		loader.load(url).catch(() => {
			// A load of the file this effect was for; wavesurfer rejects the
			// ones it aborts when it is destroyed.
			if (current) {
				window.clearTimeout(timer)
				dispatch('failed')
			}
		})
		return () => {
			current = false
			window.clearTimeout(timer)
			stopReady()
			stopError()
		}
	}, [loader, url, attempt])

	const reloadRequests = useMediaReloadRequests()
	useEffect(() => {
		dispatch('retry')
	}, [reloadRequests])

	const retry = useCallback(() => dispatch('retry'), [])
	return { status, retry }
}
